import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DiagnosisModalViewMode } from "../../../../../enums/diagnosisModalViewMode";
import {
  fetchAllDiagnosisDetailsForAnOrder,
  resetDiagnosisDetailsOnUnmount,
} from "../../../../../redux/slice/cmm/dignosisDetailsSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";
import { generateDiagnosisTableData } from "../../../diagnosisCodeModal/table/diagnosisTableData";
import { SelectedThinking } from "../types";

interface UseDiagnosisDataProps {
  rowId?: string | number;
}

interface UseDiagnosisDataReturn {
  diagnosisTableData: any[];
  loading: boolean;
  error: any;
  viewMode: DiagnosisModalViewMode;
  selectedThinking: SelectedThinking;
  handleViewThinking: (thinking: string, diagnosisId: string) => void;
  handleBackToTable: () => void;
}

export const useDiagnosisData = ({
  rowId,
}: UseDiagnosisDataProps): UseDiagnosisDataReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const diagnosisDetails = useSelector(
    (state: RootState) => state.diagnosisDetails,
  );

  const [viewMode, setViewMode] = useState<DiagnosisModalViewMode>(
    DiagnosisModalViewMode.TABLE,
  );
  const [selectedThinking, setSelectedThinking] = useState<SelectedThinking>({
    thinking: "",
    diagnosisId: "",
  });
  const [diagnosisTableData, setDiagnosisTableData] = useState<any[]>([]);

  const handleViewThinking = useCallback(
    (thinking: string, diagnosisId: string) => {
      setSelectedThinking({ thinking, diagnosisId });
      setViewMode(DiagnosisModalViewMode.THINKING);
    },
    [],
  );

  const handleBackToTable = useCallback(() => {
    setViewMode(DiagnosisModalViewMode.TABLE);
  }, []);

  // Effect to fetch diagnosis data when rowId changes
  useEffect(() => {
    if (rowId) {
      dispatch(fetchAllDiagnosisDetailsForAnOrder(String(rowId)));
    }

    return () => {
      dispatch(resetDiagnosisDetailsOnUnmount());
    };
  }, [rowId, dispatch]);

  // Effect to generate table data when diagnosis details are received
  useEffect(() => {
    if (diagnosisDetails.data) {
      setDiagnosisTableData(
        generateDiagnosisTableData(diagnosisDetails.data, handleViewThinking),
      );
    }
  }, [diagnosisDetails.data, handleViewThinking]);

  return {
    diagnosisTableData,
    loading: diagnosisDetails.loading,
    error: diagnosisDetails.error,
    viewMode,
    selectedThinking,
    handleViewThinking,
    handleBackToTable,
  };
};
